import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, PanelAPI, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnChange, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationOutsourcedServicesAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';

import './accreditation.css';

interface OutsourcedService {
  ieAccredited: string | boolean,
  cdProviderOperator: string,
  dsOutsourcedService: string,
  editable: string,
  ieStatusAccredited: boolean,
  nrEin: string,
  nrSeqaccreditation: number,
  nrSequence: number,
  outsourcedServiceName: string,
  workDescription: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: FragmentNames.ACCREDITATION_OUTSOURCED_SERVICES })
export default class AccreditationOutsourcedServicesController extends BaseController {

  @Inject(Aliases.FETCH_OUTSOURCED_SERVICES)
  private fetchOutsourcedServices: FetchAPI;
  @Inject(Aliases.FETCH_OUTSOURCED_SERVICE)
  private fetchOutsourcedService: FetchAPI;
  @Inject(Aliases.FORM_OUTSOURCED_SERVICE)
  private formOutsourcedService: FormAPI;
  @Inject(Aliases.FIELD_PROVIDER_CODE)
  private fieldProviderCode: FieldAPI;
  @Inject(Aliases.INPUT_PROVIDER_CODE)
  private inputProviderCode: InputAPI;
  @Inject(Aliases.FIELD_NAME)
  private fieldName: FieldAPI;
  @Inject(Aliases.FIELD_EMPLOYER_IDENTIFICATION_NUMBER)
  private fieldEmployerIdentificationNumber: FieldAPI;
  @Inject(Aliases.FIELD_SERVICE)
  private fieldService: FieldAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendenciesToast: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD_OUTSOURCED_SERVICES)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSeqProvider), Number(nrSeqAccreditation), Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnDynamicContentRender(Aliases.DYNAMIC_FIELD_ACCREDITED)
  public onDynamicContentRenderField(event: RenderEvent): void {
    const { data, render } = event;
    const { ieAccredited } = data as OutsourcedService;

    if (ieAccredited === 'S') {
      render((): ReactElement => this.getToken());
    }
  }

  private getToken(): ReactElement {
    return <div className="u-token-main-specialty">
      <Token value="provider.accredited" type={Type.NOTICE} />
    </div>;
  }

  private editRecord(nrSequence: number): void {
    this.fetchOutsourcedService.setParameters({ nrSequence });
    this.fetchOutsourcedService.forceFetch();
  }

  private async deleteRecord(nrSeqProvider: number, nrSeqAccreditation: number, nrSequence: number): Promise<void> {
    await HttpService.delete(Endpoint.DELETE.OUTSOURCED_SERVICES, { nrSeqProvider, nrSequence, nrSeqAccreditation })
      .then(() => {
        this.cleanForm();
        this.fetchOutsourcedServices.forceFetch();
      }).catch((response) => ToastService.open(response));
  }

  @OnClick(Aliases.BUTTON_CLEAN)
  onCleanForm(): void {
    this.cleanForm();
  }

  @OnClick(Aliases.BUTTON_CONFIRM)
  onclick(): void {
    if (this.formOutsourcedService.validate()) {
      const record = this.formOutsourcedService.getRecord() as unknown as OutsourcedService;
      const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
      
      this.formOutsourcedService.setState({ loading: true });
      HttpService.post(Endpoint.POST.OUTSOURCED_SERVICES, { nrSeqProvider, nrSeqAccreditation }, { body: { ...record } }).
        then(() => {
          this.cleanForm();
          this.formOutsourcedService.setLoading(false);
          this.fetchOutsourcedServices.forceFetch();
        }).catch((response) => {
          this.formOutsourcedService.setState({ loading: false });
          ToastService.open(response);
        });
    } else {
      this.formOutsourcedService.validate();
      this.showValidations(true);
    }
  }

  private cleanForm(): void {
    this.formOutsourcedService.setRecord({});
    this.formOutsourcedService.setData({});
    this.showValidations(false);
  }

  @OnAfterFetch(Aliases.FETCH_OUTSOURCED_SERVICE)
  onAfterFetchOutsourcedService(event: FetchEvent): void {
    const { ieStatusAccredited } = event.data as FetchAPI;
    const isValid = ieStatusAccredited ? true : false;
    this.inputProviderCode.setEnabled(isValid);
    this.fieldProviderCode.setState({ showValidations: isValid });
    this.showValidations(false);
  }

  private showValidations(validation: boolean): void {
    this.fieldName.setState({ showValidations: validation });
    this.fieldEmployerIdentificationNumber.setState({ showValidations: validation });
    this.fieldProviderCode.setState({ showValidations: validation });
    this.fieldService.setState({ showValidations: validation });
  }

  @OnChange(Aliases.FORM_OUTSOURCED_SERVICE)
  onChangeForm(event: FormAPI): void {
    this.formOutsourcedService.validate();
    const { ieStatusAccredited, cdProviderOperator } = event.value;
    this.inputProviderCode.setEnabled(ieStatusAccredited ? true : false);
    if (ieStatusAccredited) {
      this.fieldProviderCode.setValidators([{ name: 'required', options: {} }] as Validator[]);
      this.fieldProviderCode.validate();
    } else {
      if (cdProviderOperator != 0) {
        this.inputProviderCode.setValue('');
      }
      this.fieldProviderCode.setValidators([{}] as Validator[]);
      this.fieldProviderCode.validate();
    }
  }

  @OnMount(Aliases.PANEL_OUTSOURCED_SERVICE)
  OnMountPanel(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.OUTSOURCED_SERVICES);
  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD_OUTSOURCED_SERVICES)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<OutsourcedService>): void {
    const { shouldRender, data } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.OUTSOURCED_SERVICES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(toastEvent: FetchEvent): void {
    if (isEmptyArrayOrObject(toastEvent.data)) {
      this.panelPendenciesToast.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, toastEvent);
    }
  }
}

export {
  AccreditationOutsourcedServicesController,
};
